/**
 * Sprint 22 — AgentThread (ported from Sprint 3.2; restyled for the v3 side rail).
 *
 * Renders the chat thread with assistant-ui's headless primitives. The legacy
 * styling assumed a wide, dark, full-page chat (max-w-4xl, white-on-translucent);
 * this version is sized for the 380px rail and is THEME-CORRECT — layout via
 * Tailwind utility classes, all colours via v3 CSS variables (so it works in both
 * light and dark, unlike the dark-only original). Tool calls route to ToolCallCard
 * via the tools.Fallback slot so every Kuvio tool renders consistently.
 */

import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  type AssistantRuntime,
} from "@assistant-ui/react";
import type { UIMessage } from "@ai-sdk/react";
import { Send, Square } from "@icons";
import { type CSSProperties } from "react";
import { ChatMarkdown } from "./ChatMarkdown";
import { ToolCallCard } from "./ToolCallCard";
import { ChatCompletionLine } from "./ChatCompletionLine";
import { KuvioSpinner } from "./StreamingIndicator";
import { ProgressStrip } from "./ProgressStrip";
import { useChatProgress } from "../../../hooks/use-chat-progress";
import { useChatCompletion, type CompletionMeta } from "../../../hooks/use-chat-completion";
import { useChatCompaction } from "../../../hooks/use-chat-compaction";
import { useChatOAuth } from "../../../hooks/use-chat-oauth";
import { CompactionIndicator } from "./CompactionIndicator";
import { OAuthPromptCard } from "./OAuthPromptCard";
import { getErrorTitle, getErrorBody, getErrorRetryable } from "./error-classify";

interface AgentThreadProps {
  runtime: AssistantRuntime;
  chatMessages?: UIMessage[];
  chatStatus?: "submitted" | "streaming" | "ready" | "error";
  chatError?: Error;
  emptyState?: React.ReactNode;
  assistantLabel?: string;
  /** Sprint 23.9 (Fix 3) — inline "Try again" handler (re-runs the last turn). */
  onRetry?: () => void;
  /** Sprint 24.12 — "Continue" on the OAuth success card (re-runs the last turn). */
  onOAuthContinue?: () => void;
  /** №22.3.1 (D-AF4) — a to-line ("to {actor}…") rendered just above the composer,
   *  naming who answers BEFORE you speak. Optional; other consumers pass nothing. */
  toLine?: React.ReactNode;
}

export function AgentThread({
  runtime,
  chatMessages,
  chatStatus,
  chatError,
  emptyState,
  assistantLabel,
  onRetry,
  onOAuthContinue,
  toLine,
}: AgentThreadProps) {
  const progressSteps = useChatProgress(chatMessages);
  const completionMeta = useChatCompletion(chatMessages, chatStatus ?? "ready");
  const compactionStatus = useChatCompaction(chatMessages);
  const oauthPrompts = useChatOAuth(chatMessages);

  const isPlanLimit =
    !!chatError &&
    (chatError.message.includes("ai_limit") || chatError.message.includes("AI message limit"));

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadPrimitive.Root className="flex h-full w-full min-w-0 flex-col">
        <ProgressStrip steps={progressSteps} />
        <ThreadPrimitive.Viewport className="flex flex-1 flex-col overflow-y-auto">
          <div className="mt-auto flex flex-col gap-3 py-3">
            <ThreadPrimitive.Empty>
              <div className="flex flex-col items-center px-4">{emptyState}</div>
            </ThreadPrimitive.Empty>

            <ThreadPrimitive.Messages
              components={{
                UserMessage: UserMessageBubble,
                AssistantMessage: () => (
                  <AssistantMessageBubble label={assistantLabel} completionMeta={completionMeta} />
                ),
              }}
            />

            {oauthPrompts.map((p) => (
              <div key={p.connectionId} className="w-full px-3">
                <OAuthPromptCard
                  connectionId={p.connectionId}
                  connectionName={p.connectionName}
                  authUrl={p.authUrl}
                  onContinue={onOAuthContinue}
                />
              </div>
            ))}

            <CompactionIndicator status={compactionStatus} />
          </div>
        </ThreadPrimitive.Viewport>

        {chatStatus === "error" && chatError && (
          <div className="w-full px-3 pb-2">
            <div style={isPlanLimit ? planLimitCard : errorCard}>
              {isPlanLimit ? (
                <>
                  <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "var(--color-text-primary)" }}>
                    AI message limit reached
                  </p>
                  <p style={{ fontSize: 12, margin: "4px 0 0", color: "var(--color-text-secondary)" }}>
                    You've used all your AI messages this period. Add your own API key in BYOK
                    settings, or upgrade your plan for more.
                  </p>
                </>
              ) : (
                // Sprint 23.9 (Fix 3) — real title + the worker's actual reason +
                // an inline retry, instead of the old blank "Something went wrong."
                <>
                  <div style={errorHeaderStyle}>
                    <i
                      className="ti ti-alert-circle"
                      style={{ fontSize: 14, color: "var(--color-text-danger)" }}
                      aria-hidden="true"
                    />
                    <p style={errorTitleStyle}>{getErrorTitle(chatError)}</p>
                  </div>
                  <p style={errorBodyStyle}>{getErrorBody(chatError)}</p>
                  {onRetry && getErrorRetryable(chatError) && (
                    <button type="button" style={retryButtonStyle} onClick={onRetry}>
                      <i className="ti ti-refresh" style={{ fontSize: 12 }} aria-hidden="true" />
                      Try again
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {toLine}
        <ComposerArea />
      </ThreadPrimitive.Root>
    </AssistantRuntimeProvider>
  );
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

function UserMessageBubble() {
  return (
    // №22.3.2 (F2) — `chat-turn` marks a CONVERSATION turn (as opposed to a row in a
    // rollup projection). The gate counts these to prove a fresh conversation opens
    // empty and that nothing enters it uninvited.
    <MessagePrimitive.Root className="flex w-full justify-end px-3" data-testid="chat-turn" data-turn="user">
      <div style={userBubble}>
        <div style={senderLabel}>You</div>
        <MessagePrimitive.Parts
          components={{
            Text: ({ text }) => <p style={messageText}>{text}</p>,
          }}
        />
      </div>
    </MessagePrimitive.Root>
  );
}

function AssistantMessageBubble({
  label,
  completionMeta,
}: {
  label?: string;
  completionMeta?: CompletionMeta | null;
}) {
  return (
    <MessagePrimitive.Root className="flex w-full justify-start px-3" data-testid="chat-turn" data-turn="assistant">
      <div style={assistantBubble}>
        <div style={senderLabel}>{label ?? "Kuvio"}</div>
        <div style={{ minWidth: 0, color: "var(--color-text-primary)" }}>
          <MessagePrimitive.Parts
            components={{
              Text: ChatMarkdown,
              tools: { Fallback: ToolCallCard },
            }}
          />
          <MessagePrimitive.If last>
            <ThreadPrimitive.If running>
              <div className="kuvio-streaming-indicator mt-2">
                <KuvioSpinner />
              </div>
            </ThreadPrimitive.If>
            <ThreadPrimitive.If running={false}>
              {completionMeta ? <ChatCompletionLine {...completionMeta} /> : null}
            </ThreadPrimitive.If>
          </MessagePrimitive.If>
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

// №22.3.1 (P2) — assistant text renders through the ONE shared `ChatMarkdown`
// (see ./ChatMarkdown). The former inline `MarkdownText` lived here alone, which is
// how the docked task thread drifted onto a raw-text path; both now share it.

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

function ComposerArea() {
  return (
    <div style={{ flex: "0 0 auto", padding: "8px 12px 12px" }}>
      <ComposerPrimitive.Root style={composerRoot} className="flex w-full items-end gap-2">
        <ComposerPrimitive.Input
          placeholder="Ask Kuvio…"
          rows={1}
          autoFocus
          className="flex-1 resize-none bg-transparent focus:outline-none px-2 py-1.5 max-h-[120px] text-[13.5px] leading-[1.5] text-[color:var(--color-text-primary)] placeholder:text-[color:var(--color-text-tertiary)] font-[family-name:var(--font-sans)]"
        />
        <ThreadPrimitive.If running={false}>
          <ComposerPrimitive.Send asChild>
            <button type="button" style={sendBtn} title="Send">
              <Send size={15} />
            </button>
          </ComposerPrimitive.Send>
        </ThreadPrimitive.If>
        <ThreadPrimitive.If running>
          <ComposerPrimitive.Cancel asChild>
            <button type="button" style={stopBtn} title="Stop">
              <Square size={13} />
            </button>
          </ComposerPrimitive.Cancel>
        </ThreadPrimitive.If>
      </ComposerPrimitive.Root>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles (theme-aware via v3 CSS variables)
// ---------------------------------------------------------------------------

const userBubble: CSSProperties = {
  maxWidth: "88%",
  background: "var(--color-background-info)",
  border: "var(--kuvio-border-default)",
  borderRadius: 14,
  padding: "10px 12px",
};

const assistantBubble: CSSProperties = {
  maxWidth: "92%",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: 14,
  padding: "10px 12px",
};

const senderLabel: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  marginBottom: 4,
};

const messageText: CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.5,
  color: "var(--color-text-primary)",
  whiteSpace: "pre-wrap",
  margin: 0,
};

const planLimitCard: CSSProperties = {
  borderRadius: 12,
  border: "1px solid var(--color-border-secondary)",
  background: "var(--color-background-warning)",
  padding: 12,
};

const errorCard: CSSProperties = {
  borderRadius: 12,
  border: "1px solid var(--color-border-secondary)",
  background: "var(--color-background-danger)",
  padding: 12,
};

// Sprint 23.9 (Fix 3 / S23.9.09) — error card internals: header row, title,
// body (the real reason), and the retry button.
const errorHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const errorTitleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  margin: 0,
  color: "var(--color-text-primary)",
};

const errorBodyStyle: CSSProperties = {
  fontSize: 12,
  lineHeight: 1.5,
  margin: "4px 0 0",
  color: "var(--color-text-secondary)",
  wordBreak: "break-word",
};

const retryButtonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  marginTop: 10,
  padding: "5px 11px",
  borderRadius: 8,
  border: "var(--kuvio-border-default)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const composerRoot: CSSProperties = {
  borderRadius: 12,
  border: "var(--kuvio-border-default)",
  background: "var(--color-background-secondary)",
  padding: 6,
};

const sendBtn: CSSProperties = {
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
};

const stopBtn: CSSProperties = {
  ...sendBtn,
  background: "var(--color-background-tertiary)",
  color: "var(--color-text-primary)",
  border: "var(--kuvio-border-default)",
};
